import { useScrollToFocus } from '@capturi/react-utils'
import {
  Flex,
  HStack,
  SlideFade,
  Text,
  Tooltip,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { t } from '@lingui/core/macro'
import { useStore } from '@tanstack/react-store'
import { Loader } from 'copilot/components/Loader'
import { MagicBox, MagicBoxHeader } from 'copilot/components/MagicBox'
import { SummaryMarkdown } from 'copilot/components/SummaryMarkdown'
import { type FC, type RefObject, useEffect, useRef, useState } from 'react'

import { ThumbsButtons } from '../../../components/ThumbsButtons'
import { useCaptureEvent } from '../../../utils/analytics'
import { sessionStore } from '../../sessionStore'
import { RecommendedAnswer } from './RecommendedAnswer'
import { useVoiceMails } from './useVoiceMails'

type Props = {
  customer: string
  copilotToken: string
  showContent: boolean
  containerRef?: RefObject<HTMLElement | null>
}

export const Handover: FC<Props> = ({
  copilotToken,
  customer,
  showContent,
  containerRef,
}) => {
  const { organizationUid, sessionUid } = useStore(sessionStore)
  const { data: voiceMailData } = useVoiceMails(copilotToken, customer)

  const captureEvent = useCaptureEvent()
  const toast = useToast()
  const [reviewed, setReviewed] = useState(false)
  const scrollToFocus = useScrollToFocus(containerRef)

  const hasLoggedRef = useRef(false)
  useEffect(() => {
    if (hasLoggedRef.current) return
    if (
      voiceMailData?.status === 'Ok' &&
      voiceMailData.summary &&
      showContent
    ) {
      captureEvent('precall_summary_loaded', {
        sessionUid,
        customer,
        organizationUid,
        summary: voiceMailData.summary,
        summaryLength: voiceMailData.summary.length,
        keyTopic: voiceMailData?.topic || '',
        subKeyTopic: voiceMailData?.subTopic || '',
        timestamp: Date.now(),
      })
      hasLoggedRef.current = true
    }
  }, [
    voiceMailData?.status,
    voiceMailData?.summary,
    showContent,
    sessionUid,
    customer,
    organizationUid,
    voiceMailData?.topic,
    voiceMailData?.subTopic,
    captureEvent,
  ])

  if (voiceMailData == null) return null
  if (voiceMailData?.status === 'Processing') return <Loader />

  const { subTopic, summary, topic, status } = voiceMailData || {}

  return (
    <SlideFade in={status === 'Ok' && showContent}>
      <Flex alignItems="center" flexDirection="column" ref={scrollToFocus}>
        <MagicBox
          bg="productgradients.virtualAgents"
          width="clamp(240px, 464px, 100%)"
          backdropFilter="blur(64px)"
        >
          <MagicBoxHeader
            bg="gray.100"
            flexDir="row"
            justifyContent="space-between"
            alignItems="center"
            textColor="textMuted"
            p={4}
          >
            <VStack alignItems="flex-start" gap={0}>
              <Tooltip label={topic} openDelay={300} placement="top" hasArrow>
                <Text fontWeight="semibold" noOfLines={1}>
                  {topic}
                </Text>
              </Tooltip>
              <Text fontWeight="medium">{subTopic}</Text>
            </VStack>
          </MagicBoxHeader>
          <HStack alignItems="flex-start" p={3}>
            <SummaryMarkdown>
              {summary || t`No details available`}
            </SummaryMarkdown>
            {!reviewed && (
              <HStack
                gap="1"
                justify="flex-end"
                w="100%"
                alignItems="flex-start"
              >
                <ThumbsButtons
                  onUp={() => {
                    captureEvent('handover_summary_thumbsUp', {
                      organizationUid,
                      customer,
                      sessionUid,
                    })
                    setReviewed(true)
                    toast({
                      title: t`Thank you for the feedback`,
                      description: t`You are helping us to improve handover summaries`,
                      status: 'success',
                      isClosable: true,
                    })
                  }}
                  onDown={() => {
                    captureEvent('handover_summary_thumbsDown', {
                      organizationUid,
                      customer,
                      sessionUid,
                    })
                    setReviewed(true)
                    toast({
                      title: t`Thank you for the feedback`,
                      description: t`You are helping us to improve handover summaries`,
                      status: 'success',
                      isClosable: true,
                    })
                  }}
                />
              </HStack>
            )}
          </HStack>
        </MagicBox>
        {voiceMailData?.summary && voiceMailData.recommendedAnswer && (
          <RecommendedAnswer
            voiceMailData={voiceMailData}
            customer={customer}
            containerRef={containerRef}
          />
        )}
      </Flex>
    </SlideFade>
  )
}
